import { useState, useCallback, useEffect, useRef } from 'react';
import { pushSubscribe, pushUnsubscribe, getVapidPublicKey } from '../context/CommonContex';

/**
 * Convert a base64-encoded VAPID public key to a Uint8Array
 * required by the PushManager.subscribe() method.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PUSH_ENABLED_KEY = 'pushNotificationsEnabled';

export default function usePushNotifications() {
  const isSupported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  const [isEnabled, setIsEnabled] = useState(
    () => localStorage.getItem(PUSH_ENABLED_KEY) === 'true'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const subscriptionRef = useRef(null);
  const initRef = useRef(false);

  /**
   * Auto-initialize on mount:
   * - Get username from localStorage (set during login)
   * - Fetch VAPID key from backend
   * - Ask permission & subscribe automatically
   */
  useEffect(() => {
    if (!isSupported || initRef.current) return;
    initRef.current = true;

    const username = localStorage.getItem('userEmail');
    if (!username) return;

    // If already enabled, just restore the subscription ref
    if (localStorage.getItem(PUSH_ENABLED_KEY) === 'true') {
      restoreSubscription();
      return;
    }

    // If never asked before, auto-init
    if (Notification.permission === 'default') {
      autoSubscribe(username);
    } else if (Notification.permission === 'granted') {
      autoSubscribe(username);
    }

    // Watch for permission changes — e.g. user manually enables in browser settings
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'notifications' }).then((permStatus) => {
        permStatus.onchange = () => {
          if (permStatus.state === 'granted') {
            const u = localStorage.getItem('userEmail');
            if (u) autoSubscribe(u);
          }
        };
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Restore existing push subscription reference (for unsubscribe later).
   */
  const restoreSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
      if (registration) {
        const sub = await registration.pushManager.getSubscription();
        if (sub) {
          subscriptionRef.current = sub;
          setIsEnabled(true);
        } else {
          // Subscription lost (e.g. browser cleared), re-subscribe
          const username = localStorage.getItem('userEmail');
          if (username) autoSubscribe(username);
        }
      }
    } catch (err) {
      console.error('Error restoring push subscription:', err);
    }
  };

  /**
   * Automatically subscribe: fetch VAPID key from backend, register SW,
   * request permission, subscribe, and send to server.
   */
  const autoSubscribe = async (username) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch VAPID public key from backend
      const vapidResult = await getVapidPublicKey();
      if (!vapidResult.success) {
        throw new Error('Could not fetch VAPID public key from server');
      }
      const vapidKey = typeof vapidResult.data === 'string'
        ? vapidResult.data
        : vapidResult.data.publicKey || vapidResult.data;

      // 2. Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // 3. Request permission (browser will show popup first time)
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Push notification permission denied by user');
        return;
      }

      // 4. Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const applicationServerKey = urlBase64ToUint8Array(vapidKey);
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      subscriptionRef.current = subscription;

      // 5. Send subscription to backend
      const result = await pushSubscribe(username, subscription.toJSON());
      if (!result.success) {
        throw new Error(result.error || 'Subscribe failed');
      }

      localStorage.setItem(PUSH_ENABLED_KEY, 'true');
      setIsEnabled(true);
    } catch (err) {
      console.error('Auto push subscribe error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Manually disable push notifications.
   */
  const disable = useCallback(async () => {
    const username = localStorage.getItem('userEmail');
    if (!username) return false;

    setIsLoading(true);
    setError(null);

    try {
      const subscription = subscriptionRef.current;

      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        subscriptionRef.current = null;

        const result = await pushUnsubscribe(username, endpoint);
        if (!result.success) {
          throw new Error(result.error || 'Unsubscribe failed');
        }
      }

      localStorage.setItem(PUSH_ENABLED_KEY, 'false');
      setIsEnabled(false);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Manually re-enable push notifications (after user disabled them).
   */
  const enable = useCallback(async () => {
    const username = localStorage.getItem('userEmail');
    if (!username) {
      setError('Not logged in');
      return false;
    }
    await autoSubscribe(username);
    return isEnabled;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isSupported,
    isEnabled,
    isLoading,
    error,
    enable,
    disable,
  };
}
